import React from "react";
import { defineMessages, useIntl, FormattedNumber } from "react-intl";
import { Table, TableRow, TableCell, TableBody } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { LocalizedText } from "../../../components/i18n/LocalizedText.js";
import { EmptyLabel } from "../../../components/label/EmptyLabel.js";
import "../../../components/label/LabelHoc.js";
import "../../../components/table/HeaderCell.js";
import { TableHead } from "../../../components/table/TableHead.js";
import "moment-timezone";
import { isNullOrUndefined } from "../../../utils/object.js";
import { useNumberFormatter } from "../../../utils/number.js";
import { useP2PPlayerSettings } from "../hooks.js";
const localized = defineMessages({
  license: {
    id: "PlayerGlobalSettings_license",
    defaultMessage: "License Type"
  },
  country: {
    id: "PlayerGlobalSettings_country",
    defaultMessage: "Country"
  },
  kyc: {
    id: "PlayerGlobalSettings_kyc",
    defaultMessage: "KYC approved"
  },
  p2pSender: {
    id: "PlayerGlobalSettings_p2pSender",
    defaultMessage: "P2P Sender"
  },
  agentRelated: {
    id: "PlayerGlobalSettings_agentRelated",
    defaultMessage: "Agent-related"
  },
  p2p: {
    id: "PlayerGlobalSettings_p2p",
    defaultMessage: "P2P Locked"
  },
  dailyCreditLimit: {
    id: "PlayerGlobalSettings_dailyCreditLimit",
    defaultMessage: "Daily Credit Limit"
  },
  dailyTransferCount: {
    id: "PlayerGlobalSettings_dailyTransferCount",
    defaultMessage: "Daily Transfer Count"
  },
  dailyDebitLimitToAgent: {
    id: "PlayerGlobalSettings_dailyDebitLimitToAgent",
    defaultMessage: "Daily Debit Limit - to Agent"
  },
  weeklyDebitLimitToAgent: {
    id: "PlayerGlobalSettings_weeklyDebitLimitToAgent",
    defaultMessage: "Weekly Debit Limit - to Agent"
  },
  monthlyDebitLimitToAgent: {
    id: "PlayerGlobalSettings_monthlyDebitLimitToAgent",
    defaultMessage: "Monthly Debit Limit - to Agent"
  },
  dailyDebitLimitToPlayer: {
    id: "PlayerGlobalSettings_dailyDebitLimitToPlayer",
    defaultMessage: "Daily Debit Limit - to Player"
  },
  weeklyDebitLimitToPlayer: {
    id: "PlayerGlobalSettings_weeklyDebitLimitToPlayer",
    defaultMessage: "Weekly Debit Limit - to Player"
  },
  monthlyDebitLimitToPlayer: {
    id: "PlayerGlobalSettings_monthlyDebitLimitToPlayer",
    defaultMessage: "Monthly Debit Limit - to Player"
  },
  daily: {
    id: "PlayerGlobalSettings_daily",
    defaultMessage: "Daily Limit"
  },
  weekly: {
    id: "PlayerGlobalSettings_weekly",
    defaultMessage: "Weekly Limit"
  },
  monthly: {
    id: "PlayerGlobalSettings_monthly",
    defaultMessage: "Monthly Limit"
  },
  yes: {
    id: "PlayerGlobalSettings_yes",
    defaultMessage: "Yes"
  },
  no: {
    id: "PlayerGlobalSettings_no",
    defaultMessage: "No"
  },
  locked: {
    id: "PlayerGlobalSettings_locked",
    defaultMessage: "Locked"
  },
  unlocked: {
    id: "PlayerGlobalSettings_unlocked",
    defaultMessage: "Unlocked"
  },
  all: {
    id: "PlayerGlobalSettings_all",
    defaultMessage: "All"
  },
  agent: {
    id: "PlayerGlobalSettings_agent",
    defaultMessage: "Agent"
  },
  agentPlayer: {
    id: "PlayerGlobalSettings_agentPlayer",
    defaultMessage: "Agent Player"
  },
  normalPlayer: {
    id: "PlayerGlobalSettings_normalPlayer",
    defaultMessage: "Normal Player"
  }
});
const useStyles = makeStyles()((theme) => ({
  container: {
    margin: theme.spacing(2, 2, 3, 2)
  },
  headerCell: {
    fontWeight: theme.typography.fontWeightBold
  },
  lastRow: {
    "& td": {
      border: "0"
    }
  }
}));
function PlayerGlobalSettings({ uid, entityType, mode }) {
  const { formatMessage } = useIntl();
  const { classes } = useStyles();
  const showLimits = mode === "all";
  const showKycInfo = mode === "all";
  const showAgentInfo = mode === "all";
  const showLockStatus = mode === "all" || mode === "only-lock-status";
  const { item: settings } = useP2PPlayerSettings({
    entityType,
    uid,
    fields: [
      "global_settings.country",
      "global_settings.license_type",
      ...showKycInfo ? ["global_settings.is_kyc_approved"] : [],
      ...showAgentInfo ? ["global_settings.is_agent"] : [],
      ...showLockStatus ? ["global_settings.is_p2p_locked"] : [],
      ...showLimits ? [
        "global_settings.limits.daily",
        "global_settings.limits.weekly",
        "global_settings.limits.monthly"
      ] : []
    ],
    viewType: "P2PPlayerGlobalSettings"
  });
  const columns = [
    {
      title: localized.license,
      value: settings?.global_settings?.license_type
    },
    {
      title: localized.country,
      value: !isNullOrUndefined(settings?.global_settings?.country) ? settings?.global_settings?.country !== "" ? settings?.global_settings?.country : formatMessage(localized.all) : null
    },
    ...showKycInfo ? [
      {
        title: localized.kyc,
        value: !isNullOrUndefined(settings?.global_settings?.is_kyc_approved) ? settings?.global_settings?.is_kyc_approved ? localized.yes : localized.no : null
      }
    ] : [],
    ...showAgentInfo ? [
      {
        title: localized.agentRelated,
        value: !isNullOrUndefined(settings?.global_settings?.is_agent) ? settings?.global_settings?.is_agent ? localized.yes : localized.no : null
      }
    ] : [],
    ...showLockStatus ? [
      {
        title: localized.p2p,
        value: !isNullOrUndefined(settings?.global_settings?.is_p2p_locked) ? settings?.global_settings?.is_p2p_locked ? localized.locked : localized.unlocked : null
      }
    ] : [],
    ...showLimits ? [
      {
        title: localized.daily,
        value: settings?.global_settings?.limits?.daily
      },
      {
        title: localized.weekly,
        value: settings?.global_settings?.limits?.weekly
      },
      {
        title: localized.monthly,
        value: settings?.global_settings?.limits?.monthly
      }
    ] : []
  ];
  return /* @__PURE__ */ React.createElement("div", { className: classes.container }, /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement(TableRow, null, columns.map((column, index) => /* @__PURE__ */ React.createElement(TableCell, { key: index, className: classes.headerCell }, /* @__PURE__ */ React.createElement(LocalizedText, { label: column.title }))))), /* @__PURE__ */ React.createElement(TableBody, null, /* @__PURE__ */ React.createElement(TableRow, { className: classes.lastRow }, columns.map((column, index) => /* @__PURE__ */ React.createElement(TableCell, { key: index }, column.value ? typeof column.value !== "number" ? /* @__PURE__ */ React.createElement(LocalizedText, { label: column.value }) : /* @__PURE__ */ React.createElement(FormattedNumber, { value: column.value, format: "USD" }) : /* @__PURE__ */ React.createElement(EmptyLabel, { defaultSymbol: "dash" })))))));
}
function PlayerGlobalSettingsV2({ uid, entityType, mode }) {
  const { formatMessage } = useIntl();
  const { formatNumber } = useNumberFormatter();
  const { classes } = useStyles();
  const showLimits = mode === "all";
  const showKycInfo = mode === "all";
  const showSenderInfo = mode === "all";
  const showLockStatus = mode === "all" || mode === "only-lock-status";
  const senderLabelMap = {
    Agent: localized.agent,
    AgentPlayer: localized.agentPlayer,
    NormalPlayer: localized.normalPlayer
  };
  const { item: settings } = useP2PPlayerSettings({
    entityType,
    uid,
    fields: [
      "global_settings.country",
      "global_settings.license_type",
      ...showKycInfo ? ["global_settings.is_kyc_approved"] : [],
      ...showSenderInfo ? ["global_settings.p2p_sender"] : [],
      ...showLockStatus ? ["global_settings.is_p2p_locked"] : [],
      ...showLimits ? [
        "global_settings.credit_limits.daily",
        "global_settings.daily_transfer_count",
        "global_settings.debit_limits_to_agent.daily",
        "global_settings.debit_limits_to_agent.weekly",
        "global_settings.debit_limits_to_agent.monthly",
        "global_settings.debit_limits_to_player.daily",
        "global_settings.debit_limits_to_player.weekly",
        "global_settings.debit_limits_to_player.monthly"
      ] : []
    ],
    viewType: "P2PPlayerGlobalSettings"
  });
  const columns = [
    {
      title: localized.license,
      value: settings?.global_settings?.license_type
    },
    {
      title: localized.country,
      value: !isNullOrUndefined(settings?.global_settings?.country) ? settings?.global_settings?.country !== "" ? settings?.global_settings?.country : formatMessage(localized.all) : null
    },
    ...showKycInfo ? [
      {
        title: localized.kyc,
        value: !isNullOrUndefined(settings?.global_settings?.is_kyc_approved) ? settings?.global_settings?.is_kyc_approved ? formatMessage(localized.yes) : formatMessage(localized.no) : null
      }
    ] : [],
    ...showSenderInfo ? [
      {
        title: localized.p2pSender,
        value: !isNullOrUndefined(settings?.global_settings?.p2p_sender) ? formatMessage(senderLabelMap[settings.global_settings.p2p_sender]) : null
      }
    ] : [],
    ...showLimits ? [
      {
        title: localized.dailyCreditLimit,
        value: formatNumber(settings?.global_settings?.credit_limits?.daily, { style: "currency", currency: "USD" })
      }
    ] : [],
    ...showLockStatus ? [
      {
        title: localized.p2p,
        value: !isNullOrUndefined(settings?.global_settings?.is_p2p_locked) ? settings.global_settings.is_p2p_locked ? formatMessage(localized.locked) : formatMessage(localized.unlocked) : null
      }
    ] : [],
    ...showLimits ? [
      {
        title: localized.dailyTransferCount,
        value: settings?.global_settings?.daily_transfer_count?.toFixed(0)
      },
      {
        title: localized.dailyDebitLimitToAgent,
        value: formatNumber(settings?.global_settings?.debit_limits_to_agent?.daily, { style: "currency", currency: "USD" })
      },
      {
        title: localized.weeklyDebitLimitToAgent,
        value: formatNumber(settings?.global_settings?.debit_limits_to_agent?.weekly, { style: "currency", currency: "USD" })
      },
      {
        title: localized.monthlyDebitLimitToAgent,
        value: formatNumber(settings?.global_settings?.debit_limits_to_agent?.monthly, { style: "currency", currency: "USD" })
      },
      {
        title: localized.dailyDebitLimitToPlayer,
        value: formatNumber(settings?.global_settings?.debit_limits_to_player?.daily, { style: "currency", currency: "USD" })
      },
      {
        title: localized.weeklyDebitLimitToPlayer,
        value: formatNumber(settings?.global_settings?.debit_limits_to_player?.weekly, { style: "currency", currency: "USD" })
      },
      {
        title: localized.monthlyDebitLimitToPlayer,
        value: formatNumber(settings?.global_settings?.debit_limits_to_player?.monthly, { style: "currency", currency: "USD" })
      }
    ] : []
  ];
  return /* @__PURE__ */ React.createElement("div", { className: classes.container }, /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableHead, { singleLine: true }, /* @__PURE__ */ React.createElement(TableRow, null, columns.map((column, index) => /* @__PURE__ */ React.createElement(TableCell, { key: index, className: classes.headerCell }, /* @__PURE__ */ React.createElement(LocalizedText, { label: column.title }))))), /* @__PURE__ */ React.createElement(TableBody, null, /* @__PURE__ */ React.createElement(TableRow, { className: classes.lastRow }, columns.map((column, index) => /* @__PURE__ */ React.createElement(TableCell, { key: index }, column.value ? column.value : /* @__PURE__ */ React.createElement(EmptyLabel, { defaultSymbol: "dash" })))))));
}
export {
  PlayerGlobalSettings,
  PlayerGlobalSettingsV2
};
//# sourceMappingURL=PlayerGlobalSettings.js.map
